import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { rupiah, statusInfo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, Circle, Truck, ChefHat, PackageCheck, Copy } from "lucide-react";

const STEPS = [
  { key: "baru", label: "Masuk", icon: CheckCircle2 },
  { key: "cek_transfer", label: "Cek Bayar", icon: CheckCircle2 },
  { key: "diterima", label: "Diterima", icon: CheckCircle2 },
  { key: "disiapkan", label: "Disiapkan", icon: ChefHat },
  { key: "akan_dikirim", label: "Akan Dikirim", icon: Truck },
  { key: "dikirim", label: "Diantar", icon: Truck },
  { key: "selesai", label: "Selesai", icon: PackageCheck },
];

export default function Orders() {
  const { user, login } = useAuth();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen paper-bg">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-24 px-4">
          <h2 className="font-display font-black text-3xl">Masuk untuk lihat pesanan</h2>
          <button data-testid="orders-login-btn" onClick={login} className="mt-4 px-8 py-3 rounded-full bg-emerald-700 text-white font-bold">Masuk</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen paper-bg">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Pesanan Saya</h1>
        <p className="text-stone-600 mt-1">Riwayat & status pesanan warung kamu.</p>

        {orders.length === 0 ? (
          <div className="mt-10 bg-white rounded-3xl border border-[#E5DECF] p-10 text-center">
            <p className="font-display font-bold text-xl">Belum ada pesanan</p>
            <Link to="/" className="mt-3 inline-block text-emerald-700 font-bold">← Mulai belanja</Link>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {orders.map((o) => {
              const st = statusInfo(o.status);
              const activeIdx = STEPS.findIndex((s) => s.key === o.status);
              return (
                <div key={o.id} data-testid={`order-card-${o.id}`} className="bg-white rounded-3xl border border-[#E5DECF] p-5">
                  <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Order #{o.id.slice(-6).toUpperCase()}</div>
                      <div className="font-display font-bold text-lg">{new Date(o.created_at).toLocaleString("id-ID")}</div>
                    </div>
                    <span className={`tag-pill border ${st.color}`}>{st.label}</span>
                  </div>

                  {o.payment_method === "transfer" && o.transfer_amount > 0 && !["selesai", "dibatalkan"].includes(o.status) && (
                    <div data-testid={`order-transfer-info-${o.id}`} className="mt-4 rounded-2xl bg-sky-50 border border-sky-200 p-4">
                      <div className="text-xs font-bold text-sky-900 uppercase tracking-widest">🏦 Instruksi Transfer</div>
                      <div className="mt-1 font-mono font-bold text-sm text-sky-800">Bank BRI · 7114 0102 0630 502 · a.n. Abet Nigo</div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-stone-600">Transfer TEPAT sebesar:</span>
                        <span className="font-display font-black text-2xl text-sky-800">{rupiah(o.transfer_amount)}</span>
                        <button
                          data-testid={`order-copy-amount-${o.id}`}
                          onClick={() => { navigator.clipboard.writeText(String(o.transfer_amount)).then(() => toast.success(`Nominal ${rupiah(o.transfer_amount)} disalin — tempel di aplikasi BRI/BRImo`)).catch(() => toast.error("Gagal menyalin, salin manual ya")); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-700 text-white text-xs font-bold hover:bg-sky-800 active:scale-95 transition"
                        >
                          <Copy className="w-3.5 h-3.5" /> Salin Nominal
                        </button>
                      </div>
                      <div className="text-xs text-stone-600 mt-1">3 angka terakhir <b className="text-sky-800">{String(o.transfer_code).padStart(3, "0")}</b> adalah kode unik pesanan ini — jangan dibulatkan agar admin cepat menemukan mutasimu.</div>
                    </div>
                  )}

                  {o.status !== "dibatalkan" && (
                    <div data-testid="order-confirmation-tracker" className="mt-4 flex items-center gap-1">
                      {STEPS.map((s, i) => (
                        <div key={s.key} className="flex-1 flex flex-col gap-1">
                          <div className="flex items-center">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${i <= activeIdx ? "bg-emerald-700 text-white" : "bg-stone-200 text-stone-500"}`}>
                              {i <= activeIdx ? <s.icon className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                            </div>
                            {i < STEPS.length - 1 && <div className={`flex-1 h-1 ${i < activeIdx ? "bg-emerald-700" : "bg-stone-200"}`} />}
                          </div>
                          <span className={`text-[10px] font-semibold leading-tight ${i <= activeIdx ? "text-emerald-800" : "text-stone-400"}`}>{s.label}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Item ({o.items.length})</div>
                      <ul className="mt-1 space-y-1">
                        {o.items.slice(0, 4).map((it, idx) => (
                          <li key={it.product_id + idx} className="flex justify-between">
                            <span className="truncate mr-2">{it.qty}× {it.name}{it.variant_name ? ` (${it.variant_name})` : ""}{it.design_file_name ? " 🎨" : ""}</span>
                            <span className="font-semibold">{rupiah(it.qty * it.price)}</span>
                          </li>
                        ))}
                        {o.items.length > 4 && <li className="text-xs text-stone-500">+{o.items.length - 4} item lainnya</li>}
                      </ul>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Ringkasan</div>
                      <ul className="mt-1 space-y-1">
                        <li className="flex justify-between"><span>Subtotal</span><span>{rupiah(o.subtotal)}</span></li>
                        <li className="flex justify-between"><span>Ongkir + Kemasan</span><span>{rupiah(o.shipping + o.packaging)}</span></li>
                        {o.points_discount > 0 && <li className="flex justify-between text-orange-600"><span>Diskon poin</span><span>− {rupiah(o.points_discount)}</span></li>}
                        <li className="flex justify-between font-display font-black text-emerald-800 text-lg"><span>Total</span><span>{rupiah(o.total)}</span></li>
                        <li className="text-[11px] font-mono uppercase tracking-widest text-emerald-700">+{o.points_earned} Poin Member</li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Cross-promo layanan FAS Group setelah belanja */}
        <div data-testid="cross-promo-section" className="mt-10 rounded-3xl bg-gradient-to-br from-stone-900 to-emerald-950 text-white p-6 md:p-8">
          <h3 className="font-display font-black text-xl md:text-2xl">Sambil menunggu pesanan, jelajahi layanan FAS Group lainnya</h3>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "📡 FASNET WiFi", desc: "Internet cepat tetangga", href: "/fasnet" },
              { label: "🎵 FAS MUSIC", desc: "Kursus drum, piano, gitar, vokal", href: "/music" },
              { label: "🎨 FAS PRINTING", desc: "Cetak stiker, banner, jersey custom", href: "/printing" },
            ].map((s) => (
              <Link key={s.href} to={s.href} data-testid={`cross-promo-${s.href.slice(1)}`} className="rounded-2xl bg-white/10 hover:bg-white/20 transition p-4">
                <div className="font-bold text-sm">{s.label}</div>
                <div className="text-xs text-white/70 mt-1 leading-relaxed">{s.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
